import { faArrowUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFrame,
  DialogHeader,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Switch,
  Textarea,
  TextField,
} from '@plaidev/sour-react';
import { type FormEventHandler, type ReactNode, useState } from 'react';
import type { GlobalNaviUserGrants, Platform } from '../contract/index.js';
import { AUTH_ROUTE_TEMPLATES, resolveAuthHref } from '../internal/authRoutes.js';
import { resolveAccountSettingsHref } from '../internal/settingsHref.js';
import { runLogout } from '../internal/singleSignOut.js';
import type { HrefBuilder } from '../internal/urls.js';
import { AdminMenuSection, type GlobalNaviGrantActions } from './AdminMenuSection.js';
import { LogoutMenuItem } from './LogoutMenuItem.js';
import { PopoverMenuContent, PopoverMenuItem, PopoverMenuSeparator } from './popoverMenu.js';
import { useHideAdminUi } from './useHideAdminUi.js';

export type UserAccountPopoverProps = {
  grants?: GlobalNaviUserGrants;
  selfPlatform: Platform;
  requiresCrossPlatformLogout: boolean;
  buildHref: HrefBuilder;
  trigger: ReactNode;
  onOpenChange?: (open: boolean) => void;
  grantActions?: GlobalNaviGrantActions;
  onLogout?: () => void;
  /** ログアウト項目の注釈に出す「詳細はこちら」のリンク先。未指定なら QANT サポートサイトの該当記事。 */
  logoutHelpHref?: string;
};

type GrantRequestDialogState = {
  grantKey: 'systemAdmin' | 'piiAccess';
  reason: string;
  consentEvidence: string;
  consentConfirmed: boolean;
};

const isReasonRequired = (grantKey: GrantRequestDialogState['grantKey']): boolean => grantKey === 'piiAccess';

// PII アクセスは承認者がクライアント許諾を検証できるよう、許諾の確認記録と表明を必須にする。
const isClientConsentRequired = (grantKey: GrantRequestDialogState['grantKey']): boolean => grantKey === 'piiAccess';

const getGrantRequestDialogTitle = (grantKey: GrantRequestDialogState['grantKey']): string =>
  grantKey === 'systemAdmin' ? 'システム管理者権限をリクエスト' : '個人情報アクセスをリクエスト';

export function UserAccountPopover({
  grants,
  selfPlatform,
  requiresCrossPlatformLogout,
  buildHref,
  trigger,
  onOpenChange,
  grantActions,
  onLogout,
  logoutHelpHref,
}: UserAccountPopoverProps): ReactNode {
  const [grantRequestDialog, setGrantRequestDialog] = useState<GrantRequestDialogState>();
  // サブメニュー（管理用メニューの HoverCard）が閉じたポップオーバーの外に取り残されないよう、
  // ポップオーバーの開閉状態を追跡して AdminMenuSection に渡す。
  const [open, setOpen] = useState(false);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };

  const { hidden: isAdminUiHidden, toggle: toggleAdminUi } = useHideAdminUi();

  const accountProfile = resolveAccountSettingsHref({ buildHref, selfPlatform });
  const systemAdminPage = resolveAuthHref(AUTH_ROUTE_TEMPLATES.systemAdmin, buildHref);

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
      return;
    }
    const otherPlatformLabel = selfPlatform === 'karte' ? 'QANT' : 'KARTE';
    runLogout({ selfPlatform, requiresCrossPlatformLogout, buildHref }, window, (href) => {
      console.warn(`[global-navi] ${otherPlatformLabel} ログアウト用タブを開けませんでした（ポップアップブロック）`, href);
    });
  };

  const grantRequestReason = grantRequestDialog?.reason ?? '';
  const grantRequestConsentEvidence = grantRequestDialog?.consentEvidence ?? '';
  const grantRequestConsentConfirmed = grantRequestDialog?.consentConfirmed ?? false;
  const isGrantRequestReasonRequired = grantRequestDialog ? isReasonRequired(grantRequestDialog.grantKey) : false;
  const isGrantRequestConsentRequired = grantRequestDialog ? isClientConsentRequired(grantRequestDialog.grantKey) : false;
  const isGrantRequestSubmittable =
    !!grantRequestDialog &&
    (!isGrantRequestReasonRequired || !!grantRequestReason.trim()) &&
    (!isGrantRequestConsentRequired || (!!grantRequestConsentEvidence.trim() && grantRequestConsentConfirmed));

  const handleRequestGrant: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    if (!grantRequestDialog || !grantActions || !isGrantRequestSubmittable) return;

    const reason = grantRequestDialog.reason.trim();
    const consentEvidence = grantRequestDialog.consentEvidence.trim();

    grantActions.onRequest({
      grantKey: grantRequestDialog.grantKey,
      ...(reason ? { reason } : {}),
      ...(isClientConsentRequired(grantRequestDialog.grantKey)
        ? { clientConsent: { confirmed: grantRequestDialog.consentConfirmed, evidence: consentEvidence } }
        : {}),
    });
    setGrantRequestDialog(undefined);
  };

  const dialogGrantActions: GlobalNaviGrantActions | undefined = grantActions;

  return (
    <>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        {/*
          Radix の FocusScope は open 時に最初の tabbable 要素を自動フォーカスするが、
          removeLinks により <a>（アカウント設定）は候補から除外されるため、
          最初の候補が「管理用メニュー」の HoverCardTrigger になる。
          HoverCard は onFocus で open するため、放置するとポップオーバーを開いた瞬間に
          サブメニューまで開いてしまう。自動フォーカスを抑止し、ホバー（またはユーザーが
          明示的に Tab した時）まで開かないようにする。
        */}
        <PopoverContent side="right" align="end" sideOffset={18} onOpenAutoFocus={(event) => event.preventDefault()}>
          <PopoverMenuContent>
            <a href={accountProfile.href} target="_blank" rel="noopener noreferrer">
              <PopoverMenuItem>
                <span className="flex-1 truncate">アカウント設定</span>
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="shrink-0 text-[10px] text-muted" />
              </PopoverMenuItem>
            </a>

            {grants && (
              <AdminMenuSection
                grants={grants}
                selfPlatform={selfPlatform}
                parentOpen={open}
                systemAdminPageHref={systemAdminPage.href}
                grantActions={
                  dialogGrantActions
                    ? {
                        ...dialogGrantActions,
                        onRequest: (params) => {
                          if ('reason' in params && params.reason !== undefined) {
                            dialogGrantActions.onRequest(params);
                            return;
                          }
                          setGrantRequestDialog({ grantKey: params.grantKey, reason: '', consentEvidence: '', consentConfirmed: false });
                        },
                      }
                    : undefined
                }
              />
            )}

            {grants?.isEmployee && (
              <>
                <PopoverMenuSeparator />
                <PopoverMenuItem onClick={toggleAdminUi}>
                  <span className="mr-auto">Admin UI を非表示</span>
                  {/* クリックは行に委譲し二重トグルを防ぐ */}
                  <Switch checked={isAdminUiHidden} onClick={toggleAdminUi} className="pointer-events-none shrink-0" />
                </PopoverMenuItem>
              </>
            )}

            <PopoverMenuSeparator />

            <LogoutMenuItem
              onLogout={handleLogout}
              parentOpen={open}
              helpHref={logoutHelpHref}
              showCrossPlatformLogoutNote={requiresCrossPlatformLogout}
            />
          </PopoverMenuContent>
        </PopoverContent>
      </Popover>

      <Dialog open={!!grantRequestDialog} onOpenChange={(open) => !open && setGrantRequestDialog(undefined)}>
        <DialogContent>
          <DialogFrame size="sm">
            <DialogHeader title={grantRequestDialog ? getGrantRequestDialogTitle(grantRequestDialog.grantKey) : ''} />
            <DialogBody>
              <form className="flex flex-col gap-4" onSubmit={handleRequestGrant}>
                {isGrantRequestConsentRequired && (
                  <p className="text-sm text-muted">
                    個人情報アクセスの有効化には役職者の承認が必要です。特権によるアクセスは監査ログとしてクライアントに開示されるため、
                    事前にクライアントの許諾を取得し、承認者が確認できる記録を残してください。
                  </p>
                )}
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-bold" htmlFor="active-grant-request-reason">
                    リクエスト理由{isGrantRequestReasonRequired ? '' : '（任意）'}
                  </label>
                  <Textarea
                    id="active-grant-request-reason"
                    value={grantRequestReason}
                    onChange={(event) =>
                      setGrantRequestDialog((current) => (current ? { ...current, reason: event.target.value } : current))
                    }
                    placeholder="対応内容や目的を入力"
                    autoResize
                    invalid={isGrantRequestReasonRequired && grantRequestReason.length > 0 && !grantRequestReason.trim()}
                    helperText={
                      isGrantRequestReasonRequired && grantRequestReason.length > 0 && !grantRequestReason.trim()
                        ? '理由を入力してください'
                        : undefined
                    }
                  />
                </div>
                {isGrantRequestConsentRequired && (
                  <>
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-bold" htmlFor="active-grant-request-consent-evidence">
                        クライアント許諾の確認記録
                      </label>
                      <TextField
                        id="active-grant-request-consent-evidence"
                        value={grantRequestConsentEvidence}
                        onChange={(event) =>
                          setGrantRequestDialog((current) => (current ? { ...current, consentEvidence: event.target.value } : current))
                        }
                        placeholder="許諾を確認できる Slack スレッドやチケット等の URL"
                        invalid={grantRequestConsentEvidence.length > 0 && !grantRequestConsentEvidence.trim()}
                        helperText="承認者（役職者）はこの記録を見て許諾の取得を確認します"
                      />
                    </div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer" htmlFor="active-grant-request-consent-confirmed">
                      <Checkbox
                        id="active-grant-request-consent-confirmed"
                        checked={grantRequestConsentConfirmed}
                        onCheckedChange={(checked) =>
                          setGrantRequestDialog((current) => (current ? { ...current, consentConfirmed: checked === true } : current))
                        }
                      />
                      クライアントの許諾を取得済みです
                    </label>
                  </>
                )}
                <DialogActions>
                  <DialogClose asChild>
                    <Button variant="quiet" disabled={grantActions?.isMutating}>
                      キャンセル
                    </Button>
                  </DialogClose>
                  <Button type="submit" variant="primary" disabled={!isGrantRequestSubmittable} loading={grantActions?.isMutating}>
                    リクエスト
                  </Button>
                </DialogActions>
              </form>
            </DialogBody>
          </DialogFrame>
        </DialogContent>
      </Dialog>
    </>
  );
}

export type { GlobalNaviGrantActions };
